import TableButton from 'components/Buttons/TableButtons/TableButton'
import { Modal } from 'components/UI/Modal/Modal'
import React, { useEffect } from 'react'
import { CiFilter } from 'react-icons/ci'
import { useSearchParams } from 'react-router-dom'
import { EventTypeFilter } from './filters/EventTypeFilter'
import { TeamMemberFilter } from './filters/TeamMemberFilter'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import {
  FilterGroup,
  FilterModalFooterCriteria,
  FilterModalFooterCriteriaHeader,
} from 'components/Filters/components/FilterModal'
import { FilterCriteriaSelector } from 'components/Filters/components/FilterCriteriaSelector'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { EventHistoryTab } from './EventHistoryAccordion'

interface EventHistoryFilterModalProps {
  selectedTab: EventHistoryTab
  /** Subset of event types to show in the filter. Defaults to all. */
  availableEventTypes?: import('./EventHistoryFilterStore').EventTypeFilter[]
}

export function EventHistoryFilterModal({
  selectedTab,
  availableEventTypes,
}: EventHistoryFilterModalProps) {
  const [openFilterModal, setOpenFilterModal] = React.useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    applyFilters,
    syncPendingFilters,
    eventTypes,
    teamMemberIds,
    pendingCriteria,
    setPendingCriteria,
  } = useEventHistoryFilterStore()

  // Only count event type filters when on "all" tab
  const filterCount =
    (selectedTab === 'all' ? eventTypes.length : 0) + teamMemberIds.length

  useEffect(() => syncPendingFilters(), [openFilterModal, syncPendingFilters])

  const removePageFromUrlSearchParams = () => {
    const searchParamsCopy = new URLSearchParams(searchParams)
    searchParamsCopy.delete('page')
    setSearchParams(searchParamsCopy)
  }

  return (
    <>
      <TableButton
        text="More Filters"
        icon={CiFilter}
        activeValue={filterCount > 0 ? filterCount.toString() : null}
        onClick={() => setOpenFilterModal(true)}
        type="button"
        className="text-sm text-fg hover:text-fg"
      />
      <Modal
        open={openFilterModal}
        onOpenChange={(open) => setOpenFilterModal(open)}
        size="lg"
        title={'Event History Filters'}
        onAccept={() => {
          applyFilters()
          removePageFromUrlSearchParams()
          setOpenFilterModal(false)
        }}
        acceptButtonText={'Apply'}
        footerChildren={
          <>
            <FilterModalFooterCriteriaHeader>
              Show results matching
              <FilterCriteriaSelector
                value={pendingCriteria}
                onChange={setPendingCriteria}
              />
              of the following criteria:
            </FilterModalFooterCriteriaHeader>
            <FilterModalFooterCriteria>
              <EventHistoryFilterChipsPreview selectedTab={selectedTab} />
            </FilterModalFooterCriteria>
          </>
        }
      >
        <FilterGroup title="Filters">
          {/* Only show Event Type filter on "All" tab */}
          {selectedTab === 'all' && (
            <EventTypeFilter availableEventTypes={availableEventTypes} />
          )}
          <TeamMemberFilter />
        </FilterGroup>
      </Modal>
    </>
  )
}

// Helper component to show filter chips in modal footer
function EventHistoryFilterChipsPreview({
  selectedTab,
}: {
  selectedTab: EventHistoryTab
}) {
  const {
    pendingEventTypes,
    pendingTeamMemberIds,
    pendingCriteria,
    setPendingEventTypes,
    setPendingTeamMemberIds,
  } = useEventHistoryFilterStore()

  // Convert to format compatible with FilterChips
  const valueFilters: Record<
    string,
    Array<{ value: string; label: string }>
  > = {}

  // Only include event types when on "all" tab
  if (selectedTab === 'all' && pendingEventTypes.length > 0) {
    const EVENT_TYPE_LABELS: Record<string, string> = {
      deal: 'Deals',
      notes: 'Notes',
      reminder: 'Reminders',
      samples: 'Samples',
      email: 'Emails',
      contactcompany: 'Account Attributes',
    }
    valueFilters.eventTypes = pendingEventTypes.map((type) => ({
      value: type,
      label: EVENT_TYPE_LABELS[type] || type,
    }))
  }

  if (pendingTeamMemberIds.length > 0) {
    valueFilters.teamMembers = pendingTeamMemberIds.map((id) => ({
      value: id.toString(),
      label: `User ${id}`, // This will be displayed as badge
    }))
  }

  return (
    <FilterChips
      showActive={false}
      store={
        (() => ({
          pendingValueFilters: valueFilters,
          valueFilters: {},
          rangeFilters: {},
          pendingRangeFilters: {},
          clearPendingValueFilter: (key: string) => {
            if (key === 'eventTypes') {
              setPendingEventTypes([])
            } else if (key === 'teamMembers') {
              setPendingTeamMemberIds([])
            }
          },
          clearValueFilter: () => {},
          clearPendingRangeFilter: () => {},
          clearRangeFilter: () => {},
          clearAllFilters: () => {},
          clearAllPendingFilters: () => {},
          pendingZipCodeFilter: ['', 0],
          zipCodeFilter: ['', 0],
          clearPendingZipCodeFilter: () => {},
          clearZipCodeFilter: () => {},
          search: {},
          pendingSearch: {},
          clearSearch: () => {},
          clearPendingSearch: () => {},

          filterTitles: {
            eventTypes: 'Event Type',
            teamMembers: 'Team Member',
          },
          pendingCriteria,
          criteria: pendingCriteria,
        })) as any
      }
      identifier={'EVENT_HISTORY' as any}
      className="ml-0"
    />
  )
}
